// Dibujos para «Estado sensores → Gráficas»: casa, termómetro, gota, depósito, cilindros, aparatos,
// reloj de 24 h y calendario de colores. Todo en SVG, sin librerías.
import React, { useId, useState } from "react";

export const V = {
  ink: "#1F271D", ink2: "#3F473B", ink3: "#5C6457", line: "#D9DED3", soft: "#EEF0EB",
  luz: "#B88400", luzVivo: "#F2B705", luzClaro: "#FFF1C7", luzOsc: "#8A5A00",
  frio: "#2A78D6", frioClaro: "#DCEBFB", frioOsc: "#1C4F95",
  confort: "#3A7D1C", confortClaro: "#D6ECC6",
  calor: "#E2622B", calorClaro: "#FCE0D2", calorOsc: "#A8380F",
  agua: "#1F7FD1", aguaClaro: "#D6E9FA", aguaOsc: "#0F5A9C",
  aparato: "#6250D6", aparatoClaro: "#E6E2FA", aparatoOsc: "#3F2FA0",
  ok: "#2E7D1F", okClaro: "#E2F1D8", warn: "#9A6400", warnClaro: "#FDF0CF", bad: "#C0362C", badClaro: "#FBE1DD",
};

const limpio = (id) => id.replace(/[^a-zA-Z0-9_-]/g, "");
const useUid = () => limpio(useId());
const nf = (v, d = 1) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? "—" : Number(v).toLocaleString("es-ES", { maximumFractionDigits: d }));

// ── Color ───────────────────────────────────────────────────────────────────
const hex = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
export function mezcla(a, b, t) {
  const x = hex(a), y = hex(b), k = Math.max(0, Math.min(1, t));
  return "#" + x.map((v, i) => Math.round(v + (y[i] - v) * k).toString(16).padStart(2, "0")).join("");
}
// Texto blanco u oscuro según lo claro que sea el fondo
export function tinta(bg) {
  const [r, g, b] = hex(bg).map((v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.32 ? V.ink : "#FFFFFF";
}
// Frío (azul) · cómodo (verde) · calor (naranja), más intenso cuanto más se aleja de la zona cómoda
export function colorTemp(t, conf) {
  if (t === null || t === undefined) return null;
  const { tmin = 20, tmax = 25 } = conf || {};
  if (t < tmin) return mezcla("#CFE2FA", V.frioOsc, (tmin - t) / 6);
  if (t > tmax) return mezcla("#FAD3BF", V.calorOsc, (t - tmax) / 6);
  return "#BFE0A8";
}
export const estadoTemp = (t, conf) => (t < conf.tmin ? "frio" : t > conf.tmax ? "calor" : "confort");

// ── Casa isométrica con lo que pasa ahora ───────────────────────────────────
// Puntos de la fachada (u a lo largo, v en altura) y del hastial (w en profundidad)
const F = (u, v) => [104.7 + 0.866 * u, 180 + 0.5 * u - v];
const G = (w, v) => [338.6 - 0.866 * w, 205 + 0.5 * w - v];
const pts = (...p) => p.map((q) => q.join(",")).join(" ");
const VB = { x: 40, y: 0, w: 420, h: 320 };
export const ANCLAS = { clima: F(35, 80), aparatos: F(127, 80), luz: G(81, 24), agua: [385, 166] };
const ABAJO = { luz: true };

export function Casa3D({ luces = false, consumo = false, nivel = null, deposito = false, noche = false, badges = [], conBadges = true, alto = 330 }) {
  const u = useUid();
  const ventana = (a, b, c, d, k) => (
    <g key={k}>
      <polygon points={pts(a, b, c, d)} fill={luces ? `url(#vluz${u})` : `url(#vcri${u})`} stroke="#2F382C" strokeWidth="1.6" strokeLinejoin="round" />
      <line x1={(a[0] + b[0]) / 2} y1={(a[1] + b[1]) / 2} x2={(c[0] + d[0]) / 2} y2={(c[1] + d[1]) / 2} stroke="#FFFFFF" strokeWidth="2" opacity="0.9" />
      <line x1={(a[0] + d[0]) / 2} y1={(a[1] + d[1]) / 2} x2={(b[0] + c[0]) / 2} y2={(b[1] + c[1]) / 2} stroke="#FFFFFF" strokeWidth="2" opacity="0.9" />
    </g>
  );
  const nv = Math.max(0, Math.min(100, nivel || 0));
  const tq = { cx: 385, base: 240, rx: 24, ry: 8, h: 66 };
  const yAgua = tq.base - (tq.h * nv) / 100;
  return (
    <div style={{ position: "relative", width: "100%", maxWidth: 560, margin: "0 auto" }}>
      <svg viewBox={`${VB.x} ${VB.y} ${VB.w} ${VB.h}`} width="100%" style={{ display: "block", maxHeight: alto }} role="img"
        aria-label={`Dibujo del alojamiento: luces ${luces ? "encendidas" : "apagadas"}${deposito ? `, depósito al ${Math.round(nv)} %` : ""}`}>
        <defs>
          <linearGradient id={`vluz${u}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#FFF4B8" /><stop offset="1" stopColor="#FFC93C" /></linearGradient>
          <linearGradient id={`vcri${u}`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={noche ? "#4A5D80" : "#D8EBF7"} /><stop offset="1" stopColor={noche ? "#2B3A57" : "#9CC3DE"} /></linearGradient>
          <linearGradient id={`tej${u}`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#C8653C" /><stop offset="1" stopColor="#9C4424" /></linearGradient>
          <linearGradient id={`tq${u}`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#B9C4CC" /><stop offset="0.4" stopColor="#F4F7F9" /><stop offset="1" stopColor="#9AA7B1" /></linearGradient>
          <linearGradient id={`ag${u}`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor={V.aguaOsc} /><stop offset="0.4" stopColor="#4FA3E8" /><stop offset="1" stopColor={V.aguaOsc} /></linearGradient>
          <radialGradient id={`brillo${u}`}><stop offset="0" stopColor="#FFE680" stopOpacity="0.85" /><stop offset="1" stopColor="#FFE680" stopOpacity="0" /></radialGradient>
        </defs>
        {/* cielo: sol de día, luna y estrellas de noche */}
        {noche ? (
          <g>
            <circle cx="88" cy="40" r="17" fill="#F4F1DE" /><circle cx="96" cy="34" r="15" fill="#22304C" />
            {[[150, 22], [400, 30], [440, 70], [60, 95], [250, 14], [330, 40]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={i % 2 ? 1.6 : 2.2} fill="#F4F1DE" opacity="0.85" />)}
          </g>
        ) : (
          <g>
            <circle cx="86" cy="42" r="18" fill="#FFCC33" stroke="#F2A900" strokeWidth="2" />
            {Array.from({ length: 8 }, (_, i) => { const a = (i * Math.PI) / 4; return <line key={i} x1={86 + Math.cos(a) * 25} y1={42 + Math.sin(a) * 25} x2={86 + Math.cos(a) * 32} y2={42 + Math.sin(a) * 32} stroke="#F2A900" strokeWidth="3" strokeLinecap="round" />; })}
            <g fill="#FFFFFF" opacity="0.95"><ellipse cx="404" cy="48" rx="34" ry="11" /><circle cx="390" cy="40" r="13" /><circle cx="410" cy="34" r="16" /><circle cx="426" cy="43" r="10" /></g>
          </g>
        )}
        {/* terreno */}
        <polygon points={pts([200, 95], [425, 225], [278, 310], [52.8, 180])} fill={noche ? "#3E5A35" : "#CFE3B8"} stroke={noche ? "#2E4527" : "#9FC27F"} strokeWidth="2" strokeLinejoin="round" />
        <polygon points={pts([104.7, 184], [243.3, 264], [338.6, 209], [356, 219], [251, 280], [98, 192])} fill="#000" opacity="0.12" />
        {/* árbol */}
        <rect x="70" y="160" width="7" height="24" rx="2" fill="#7A4E2D" />
        <circle cx="73.5" cy="146" r="22" fill={noche ? "#2F5E2A" : "#5E9B3A"} stroke="#2F382C" strokeWidth="1.6" />
        <circle cx="66" cy="139" r="7" fill="#FFFFFF" opacity="0.18" />
        {/* paredes */}
        <polygon points={pts([104.7, 180], [243.3, 260], [243.3, 170], [104.7, 90])} fill={noche ? "#C9C2B4" : "#F6F1E7"} stroke="#2F382C" strokeWidth="1.8" strokeLinejoin="round" />
        <polygon points={pts([243.3, 260], [338.6, 205], [338.6, 115], [291, 92.5], [243.3, 170])} fill={noche ? "#A39C8F" : "#DDD3C2"} stroke="#2F382C" strokeWidth="1.8" strokeLinejoin="round" />
        {/* tejado */}
        <polygon points={pts([92.6, 93], [245, 181], [297.9, 96.5], [145.5, 8.5])} fill={`url(#tej${u})`} stroke="#2F382C" strokeWidth="1.8" strokeLinejoin="round" />
        {[0.25, 0.5, 0.75].map((k) => (
          <line key={k} x1={92.6 + (145.5 - 92.6) * k} y1={93 + (8.5 - 93) * k} x2={245 + (297.9 - 245) * k} y2={181 + (96.5 - 181) * k} stroke="#7E3518" strokeWidth="1.2" opacity="0.55" />
        ))}
        <g stroke="#2F382C" strokeWidth="1.5" strokeLinejoin="round">
          <polygon points={pts([233.4, 90.8], [245.5, 97.8], [245.5, 71.8], [233.4, 64.8])} fill="#A79E90" />
          <polygon points={pts([245.5, 97.8], [254.2, 83.7], [254.2, 66.8], [245.5, 71.8])} fill="#857C6F" />
          <polygon points={pts([233.4, 64.8], [245.5, 71.8], [254.2, 66.8], [242.1, 59.8])} fill="#5E574D" />
        </g>
        {/* brillo de las ventanas con luz */}
        {luces && <><ellipse cx="135" cy="140" rx="40" ry="34" fill={`url(#brillo${u})`} /><ellipse cx="215" cy="186" rx="40" ry="34" fill={`url(#brillo${u})`} /></>}
        {ventana(F(18, 42), F(52, 42), F(52, 74), F(18, 74), "a")}
        {ventana(F(110, 42), F(144, 42), F(144, 74), F(110, 74), "b")}
        {ventana(G(25, 40), G(55, 40), G(55, 70), G(25, 70), "c")}
        {/* puerta */}
        <polygon points={pts(F(68, 0), F(94, 0), F(94, 54), F(68, 54))} fill="#7A4E2D" stroke="#2F382C" strokeWidth="1.6" strokeLinejoin="round" />
        <circle cx={F(89, 26)[0]} cy={F(89, 26)[1]} r="2" fill="#F2B705" />
        {/* contador de luz en el hastial */}
        <polygon points={pts(G(70, 28), G(92, 28), G(92, 58), G(70, 58))} fill={consumo ? "#FFE08A" : "#F2F2EE"} stroke="#2F382C" strokeWidth="1.6" strokeLinejoin="round" />
        <path d={`M${G(80, 54).join(" ")} L${G(85, 44).join(" ")} L${G(79, 44).join(" ")} L${G(83, 33).join(" ")}`} fill="none" stroke={consumo ? "#B86A00" : "#7D8478"} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />
        {/* depósito de agua */}
        {deposito && (
          <g>
            <ellipse cx={tq.cx} cy={tq.base + 2} rx={tq.rx + 6} ry={tq.ry + 2} fill="#000" opacity="0.15" />
            <rect x={tq.cx - tq.rx} y={tq.base - tq.h} width={tq.rx * 2} height={tq.h} fill={`url(#tq${u})`} />
            <ellipse cx={tq.cx} cy={tq.base} rx={tq.rx} ry={tq.ry} fill={`url(#tq${u})`} stroke="#2F382C" strokeWidth="1.6" />
            {nv > 0 && <>
              <rect x={tq.cx - tq.rx} y={yAgua} width={tq.rx * 2} height={tq.base - yAgua} fill={`url(#ag${u})`} opacity="0.92" />
              <ellipse cx={tq.cx} cy={tq.base} rx={tq.rx} ry={tq.ry} fill={`url(#ag${u})`} opacity="0.92" />
              <ellipse cx={tq.cx} cy={yAgua} rx={tq.rx} ry={tq.ry} fill="#8CC8F5" stroke={V.aguaOsc} strokeWidth="1" />
            </>}
            <line x1={tq.cx - tq.rx} y1={tq.base - tq.h} x2={tq.cx - tq.rx} y2={tq.base} stroke="#2F382C" strokeWidth="1.6" />
            <line x1={tq.cx + tq.rx} y1={tq.base - tq.h} x2={tq.cx + tq.rx} y2={tq.base} stroke="#2F382C" strokeWidth="1.6" />
            <ellipse cx={tq.cx} cy={tq.base - tq.h} rx={tq.rx} ry={tq.ry} fill="#C7D1D8" stroke="#2F382C" strokeWidth="1.6" />
            <rect x={tq.cx - tq.rx + 6} y={tq.base - tq.h + 10} width="4" height={tq.h - 16} rx="2" fill="#FFFFFF" opacity="0.6" />
          </g>
        )}
      </svg>
      {conBadges && badges.filter(Boolean).map((b) => {
        const [x, y] = ANCLAS[b.donde];
        return (
          <div key={b.donde} style={{ position: "absolute", left: `${((x - VB.x) / VB.w) * 100}%`, top: `${((y - VB.y) / VB.h) * 100}%`, transform: ABAJO[b.donde] ? "translate(-50%, 6px)" : "translate(-50%, calc(-100% - 6px))", pointerEvents: "none", display: "flex", flexDirection: ABAJO[b.donde] ? "column-reverse" : "column" }}>
            <div style={{ background: "#FFFFFF", border: `2px solid ${b.color}`, borderRadius: 12, padding: "4px 10px", boxShadow: "0 6px 16px rgba(20,30,15,0.22)", whiteSpace: "nowrap", textAlign: "center" }}>
              <div style={{ fontSize: 15.5, fontWeight: 800, color: V.ink, lineHeight: 1.15 }}>{b.valor}</div>
              {b.texto && <div style={{ fontSize: 11.5, fontWeight: 600, color: b.color, lineHeight: 1.2 }}>{b.texto}</div>}
            </div>
            <div style={{ width: 0, height: 0, margin: "0 auto", borderLeft: "7px solid transparent", borderRight: "7px solid transparent", [ABAJO[b.donde] ? "borderBottom" : "borderTop"]: `8px solid ${b.color}` }} />
          </div>
        );
      })}
    </div>
  );
}

// ── Termómetro de cristal ───────────────────────────────────────────────────
export function Termometro3D({ valor, tmin = 20, tmax = 25, alto = 150 }) {
  const u = useUid();
  const lo = Math.min(10, tmin - 6, Number.isFinite(valor) ? Math.floor(valor) - 2 : 10), hi = Math.max(32, tmax + 5, Number.isFinite(valor) ? Math.ceil(valor) + 2 : 32);
  const y = (t) => 122 - ((t - lo) / (hi - lo)) * 108;
  const v = Number.isFinite(valor) ? Math.max(lo, Math.min(hi, valor)) : null;
  const est = v === null ? null : v < tmin ? "frio" : v > tmax ? "calor" : "confort";
  const col = { frio: V.frio, calor: V.calor, confort: V.confort }[est] || "#A9AEA2";
  const marcas = []; for (let t = Math.ceil(lo / 5) * 5; t <= hi; t += 5) marcas.push(t);
  return (
    <svg viewBox="0 0 84 172" height={alto} style={{ display: "block", flexShrink: 0 }} role="img" aria-label={v === null ? "Sin lectura" : `Termómetro a ${nf(valor)} grados`}>
      <defs>
        <linearGradient id={`vid${u}`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#E3E8EC" /><stop offset="0.45" stopColor="#FFFFFF" /><stop offset="1" stopColor="#C9D1D7" /></linearGradient>
        <linearGradient id={`mer${u}`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor={mezcla(col, "#000000", 0.25)} /><stop offset="0.45" stopColor={mezcla(col, "#FFFFFF", 0.3)} /><stop offset="1" stopColor={mezcla(col, "#000000", 0.3)} /></linearGradient>
        <radialGradient id={`bul${u}`} cx="0.38" cy="0.35" r="0.7"><stop offset="0" stopColor={mezcla(col, "#FFFFFF", 0.45)} /><stop offset="1" stopColor={mezcla(col, "#000000", 0.25)} /></radialGradient>
      </defs>
      {/* zona cómoda */}
      <rect x="10" y={y(tmax)} width="7" height={y(tmin) - y(tmax)} rx="3.5" fill={V.confort} />
      <text x="13.5" y={y(tmax) - 5} textAnchor="middle" fontSize="11" fontWeight="800" fill={V.confort}>OK</text>
      <rect x="25" y="6" width="22" height="132" rx="11" fill={`url(#vid${u})`} stroke="#8E978A" strokeWidth="1.5" />
      <circle cx="36" cy="148" r="20" fill={`url(#vid${u})`} stroke="#8E978A" strokeWidth="1.5" />
      <rect x="30" y="12" width="12" height="128" rx="6" fill="#F3F5F2" />
      {v !== null && <rect x="31" y={y(v)} width="10" height={146 - y(v)} rx="5" fill={`url(#mer${u})`} />}
      <circle cx="36" cy="148" r="14" fill={v === null ? "#D5D9D1" : `url(#bul${u})`} />
      <rect x="32" y="14" width="3" height="118" rx="1.5" fill="#FFFFFF" opacity="0.75" />
      <ellipse cx="30" cy="142" rx="3.5" ry="6" fill="#FFFFFF" opacity="0.6" />
      {marcas.map((t) => (
        <g key={t}>
          <line x1="49" x2={t % 10 ? 53 : 57} y1={y(t)} y2={y(t)} stroke={V.ink2} strokeWidth="1.6" />
          {t % 10 === 0 && <text x="58" y={y(t) + 4.5} fontSize="13" fill={V.ink} fontWeight="700">{t}°</text>}
        </g>
      ))}
    </svg>
  );
}

// ── Gota de humedad ─────────────────────────────────────────────────────────
const GOTA = "M50 6 C50 6 13 55 13 85 A37 37 0 0 0 87 85 C87 55 50 6 50 6 Z";
export function Gota({ pct, alto = 110 }) {
  const u = useUid();
  const p = Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : null;
  const y = 122 - ((p || 0) / 100) * 116;
  return (
    <svg viewBox="0 0 100 128" height={alto} style={{ display: "block", flexShrink: 0 }} role="img" aria-label={p === null ? "Sin lectura" : `Humedad ${Math.round(p)} %`}>
      <defs>
        <clipPath id={`gc${u}`}><path d={GOTA} /></clipPath>
        <linearGradient id={`gg${u}`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#6CB8F2" /><stop offset="1" stopColor={V.aguaOsc} /></linearGradient>
      </defs>
      <path d={GOTA} fill="#EEF5FC" />
      {p !== null && (
        <g clipPath={`url(#gc${u})`}>
          <path d={`M0 ${y} Q 16 ${y - 6} 33 ${y} T 66 ${y} T 100 ${y} V 130 H 0 Z`} fill={`url(#gg${u})`} />
        </g>
      )}
      <path d={GOTA} fill="none" stroke={V.aguaOsc} strokeWidth="3" />
      <ellipse cx="33" cy="72" rx="6" ry="15" fill="#FFFFFF" opacity="0.6" transform="rotate(18 33 72)" />
      {p !== null && <text x="50" y="96" textAnchor="middle" fontSize="22" fontWeight="800" fill={y < 84 ? "#FFFFFF" : V.aguaOsc}>{Math.round(p)}%</text>}
    </svg>
  );
}

// ── Depósito cilíndrico ─────────────────────────────────────────────────────
export function Deposito3D({ pct, alto = 150, minimo }) {
  const u = useUid();
  const p = Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : null;
  const top = 22, base = 134, rx = 44, ry = 12, cx = 60;
  const yw = base - ((base - top) * (p || 0)) / 100;
  const bajo = p !== null && p <= (minimo || 10);
  return (
    <svg viewBox="0 0 132 156" height={alto} style={{ display: "block", flexShrink: 0 }} role="img" aria-label={p === null ? "Sin lectura" : `Depósito al ${Math.round(p)} %`}>
      <defs>
        <linearGradient id={`dc${u}`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#B7C2CA" /><stop offset="0.4" stopColor="#F7F9FA" /><stop offset="1" stopColor="#97A4AE" /></linearGradient>
        <linearGradient id={`da${u}`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor={V.aguaOsc} /><stop offset="0.4" stopColor="#52A6EA" /><stop offset="1" stopColor={V.aguaOsc} /></linearGradient>
      </defs>
      <ellipse cx={cx} cy={base + 6} rx={rx + 8} ry={ry} fill="#000" opacity="0.12" />
      <rect x={cx - rx} y={top} width={rx * 2} height={base - top} fill={`url(#dc${u})`} />
      <ellipse cx={cx} cy={base} rx={rx} ry={ry} fill={`url(#dc${u})`} />
      {p > 0 && <>
        <rect x={cx - rx} y={yw} width={rx * 2} height={base - yw} fill={`url(#da${u})`} opacity="0.95" />
        <ellipse cx={cx} cy={base} rx={rx} ry={ry} fill={`url(#da${u})`} opacity="0.95" />
        <ellipse cx={cx} cy={yw} rx={rx} ry={ry} fill="#9AD0F7" stroke={V.aguaOsc} strokeWidth="1.2" />
      </>}
      <path d={`M${cx - rx} ${top} V${base} A${rx} ${ry} 0 0 0 ${cx + rx} ${base} V${top}`} fill="none" stroke="#2F382C" strokeWidth="2" />
      <ellipse cx={cx} cy={top} rx={rx} ry={ry} fill="#C9D3DA" stroke="#2F382C" strokeWidth="2" />
      <ellipse cx={cx} cy={top} rx={14} ry={4} fill="#9AA7B1" stroke="#2F382C" strokeWidth="1.2" />
      <rect x={cx - rx + 8} y={top + 12} width="6" height={base - top - 18} rx="3" fill="#FFFFFF" opacity="0.55" />
      {[25, 50, 75].map((k) => { const yy = base - ((base - top) * k) / 100; return (
        <g key={k}><line x1={cx + rx + 2} x2={cx + rx + 9} y1={yy} y2={yy} stroke={V.ink3} strokeWidth="1.4" /><text x={cx + rx + 11} y={yy + 3.5} fontSize="9.5" fill={V.ink2} fontWeight="600">{k}</text></g>
      ); })}
      {p !== null && (
        <text x={cx} y={(top + base) / 2 + 12} textAnchor="middle" fontSize="24" fontWeight="800" fill={yw < (top + base) / 2 + 4 ? "#FFFFFF" : bajo ? V.bad : V.ink}
          stroke={yw < (top + base) / 2 + 4 ? "none" : "#FFFFFF"} strokeWidth="4" paintOrder="stroke">{Math.round(p)}%</text>
      )}
    </svg>
  );
}

// ── Dos cilindros: este periodo frente al anterior ──────────────────────────
export function Cilindros({ actual, anterior, color, etqActual, etqAnterior, unidad, alto = 140, dec = 1 }) {
  const u = useUid();
  const max = Math.max(actual || 0, anterior || 0, 1e-9);
  const base = 122, hMax = 80, rx = 28, ry = 8;
  const cil = (cx, v, c, k) => {
    const h = v > 0 ? Math.max(5, (v / max) * hMax) : 2;
    const top = base - h;
    return (
      <g key={k}>
        <defs>
          <linearGradient id={`c${k}${u}`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor={mezcla(c, "#000000", 0.28)} /><stop offset="0.42" stopColor={mezcla(c, "#FFFFFF", 0.28)} /><stop offset="1" stopColor={mezcla(c, "#000000", 0.32)} /></linearGradient>
        </defs>
        <ellipse cx={cx} cy={base + 3} rx={rx + 6} ry={ry} fill="#000" opacity="0.1" />
        <rect x={cx - rx} y={top} width={rx * 2} height={h} fill={`url(#c${k}${u})`} />
        <ellipse cx={cx} cy={base} rx={rx} ry={ry} fill={`url(#c${k}${u})`} />
        <ellipse cx={cx} cy={top} rx={rx} ry={ry} fill={mezcla(c, "#FFFFFF", 0.42)} stroke={mezcla(c, "#000000", 0.2)} strokeWidth="1" />
        <text x={cx} y={top - ry - 6} textAnchor="middle" fontSize="16" fontWeight="800" fill={V.ink}>{nf(v, dec)}{unidad ? ` ${unidad}` : ""}</text>
      </g>
    );
  };
  return (
    <svg viewBox="0 0 200 156" height={alto} style={{ display: "block", flexShrink: 0 }} role="img" aria-label={`${etqActual}: ${nf(actual, dec)} ${unidad}; ${etqAnterior}: ${nf(anterior, dec)} ${unidad}`}>
      {cil(55, anterior || 0, "#A3AA9E", "p")}
      {cil(145, actual || 0, color, "a")}
      <text x="55" y="148" textAnchor="middle" fontSize="13.5" fill={V.ink3} fontWeight="700">{etqAnterior}</text>
      <text x="145" y="148" textAnchor="middle" fontSize="13.5" fill={V.ink} fontWeight="800">{etqActual}</text>
    </svg>
  );
}

// ── Aparatos ────────────────────────────────────────────────────────────────
export function Bombilla({ on, alto = 76 }) {
  const u = useUid();
  return (
    <svg viewBox="0 0 64 88" height={alto} style={{ display: "block", flexShrink: 0, overflow: "visible" }} role="img" aria-label={on ? "Luz encendida" : "Luz apagada"}>
      <defs>
        <radialGradient id={`bo${u}`} cx="0.4" cy="0.35" r="0.75"><stop offset="0" stopColor={on ? "#FFFBE0" : "#FFFFFF"} /><stop offset="1" stopColor={on ? "#FFC21A" : "#CBD1C6"} /></radialGradient>
        <radialGradient id={`bh${u}`}><stop offset="0" stopColor="#FFE16B" stopOpacity="0.9" /><stop offset="1" stopColor="#FFE16B" stopOpacity="0" /></radialGradient>
        <linearGradient id={`bb${u}`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#7D8478" /><stop offset="0.45" stopColor="#E3E6E0" /><stop offset="1" stopColor="#6A7166" /></linearGradient>
      </defs>
      {on && <circle cx="32" cy="30" r="36" fill={`url(#bh${u})`} />}
      <path d="M32 4 C16 4 6 16 6 30 C6 41 13 47 18 54 C20 57 21 60 21 63 H43 C43 60 44 57 46 54 C51 47 58 41 58 30 C58 16 48 4 32 4 Z"
        fill={`url(#bo${u})`} stroke={on ? "#B97800" : "#7D8478"} strokeWidth="2.2" />
      <path d="M25 46 L28 32 L32 42 L36 32 L39 46" fill="none" stroke={on ? "#C25400" : "#8E9489"} strokeWidth="2" strokeLinejoin="round" />
      <ellipse cx="21" cy="22" rx="5" ry="9" fill="#FFFFFF" opacity="0.75" transform="rotate(20 21 22)" />
      <rect x="20" y="63" width="24" height="6" rx="2.5" fill={`url(#bb${u})`} stroke="#4B5347" strokeWidth="1" />
      <rect x="20" y="69.5" width="24" height="6" rx="2.5" fill={`url(#bb${u})`} stroke="#4B5347" strokeWidth="1" />
      <path d="M24 76 H40 L36 84 H28 Z" fill="#3F473B" />
    </svg>
  );
}
export function Enchufe({ on, alto = 76 }) {
  const u = useUid();
  return (
    <svg viewBox="0 0 64 88" height={alto} style={{ display: "block", flexShrink: 0 }} role="img" aria-label={on ? "Enchufe encendido" : "Enchufe apagado"}>
      <defs>
        <linearGradient id={`eb${u}`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#FFFFFF" /><stop offset="1" stopColor="#C9CFC4" /></linearGradient>
        <radialGradient id={`el${u}`}><stop offset="0" stopColor={on ? "#9BFF8A" : "#E3E6E0"} /><stop offset="1" stopColor={on ? "#2E9E1F" : "#A9AEA2"} /></radialGradient>
      </defs>
      <ellipse cx="34" cy="80" rx="24" ry="5" fill="#000" opacity="0.12" />
      <rect x="12" y="12" width="44" height="64" rx="13" fill="#B9C0B3" />
      <rect x="8" y="8" width="44" height="64" rx="13" fill={`url(#eb${u})`} stroke="#4B5347" strokeWidth="2" />
      <circle cx="30" cy="34" r="13" fill="#F1F3EE" stroke="#8E9489" strokeWidth="1.5" />
      <circle cx="25" cy="34" r="3.2" fill="#2F382C" /><circle cx="35" cy="34" r="3.2" fill="#2F382C" />
      {on && <circle cx="30" cy="58" r="9" fill="#9BFF8A" opacity="0.35" />}
      <circle cx="30" cy="58" r="4.5" fill={`url(#el${u})`} stroke="#4B5347" strokeWidth="1" />
    </svg>
  );
}
export function Valvula({ on, alto = 76 }) {
  const u = useUid();
  return (
    <svg viewBox="0 0 88 88" height={alto} style={{ display: "block", flexShrink: 0 }} role="img" aria-label={on ? "Válvula abierta" : "Válvula cerrada"}>
      <defs>
        <linearGradient id={`vt${u}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#E3E8EC" /><stop offset="0.45" stopColor="#FFFFFF" /><stop offset="1" stopColor="#8E9AA4" /></linearGradient>
        <radialGradient id={`vc${u}`} cx="0.4" cy="0.35" r="0.7"><stop offset="0" stopColor="#F7D774" /><stop offset="1" stopColor="#B07D12" /></radialGradient>
      </defs>
      <rect x="2" y="52" width="72" height="16" rx="3" fill={`url(#vt${u})`} stroke="#4B5347" strokeWidth="1.8" />
      <circle cx="38" cy="60" r="15" fill={`url(#vc${u})`} stroke="#4B5347" strokeWidth="1.8" />
      <rect x="35" y="34" width="6" height="12" fill="#8E9489" stroke="#4B5347" strokeWidth="1.2" />
      {on
        ? <rect x="14" y="29" width="48" height="10" rx="5" fill={V.agua} stroke="#2F382C" strokeWidth="1.6" />
        : <rect x="33" y="3" width="10" height="36" rx="5" fill={V.bad} stroke="#2F382C" strokeWidth="1.6" />}
      <circle cx="38" cy="34" r="4" fill="#3F473B" />
      {on ? [[80, 66], [83, 77], [77, 85]].map(([x, y], i) => (
        <path key={i} d={`M${x} ${y - 5} C${x} ${y - 5} ${x - 3.5} ${y} ${x - 3.5} ${y + 1} a3.5 3.5 0 0 0 7 0 C${x + 3.5} ${y} ${x} ${y - 5} ${x} ${y - 5} Z`} fill="#4FA3E8" stroke={V.aguaOsc} strokeWidth="1" />
      )) : <rect x="74" y="50" width="6" height="20" rx="2" fill="#4B5347" />}
    </svg>
  );
}
export function DibujoAparato({ nombre, tipo, on, alto }) {
  if (/valv|grifo|riego|agua/i.test(nombre || "")) return <Valvula on={on} alto={alto} />;
  if (/plug|enchufe/i.test(nombre || "") || String(tipo || "").startsWith("energia")) return <Enchufe on={on} alto={alto} />;
  return <Bombilla on={on} alto={alto} />;
}

// ── Reloj de 24 horas: cuánto (barras) o qué color (anillo) a cada hora ─────
const ang = (h) => (h / 24) * Math.PI * 2 - Math.PI / 2;
function cuña(h, ri, ro, gap = 0.012) {
  const a0 = ang(h) + gap, a1 = ang(h + 1) - gap;
  const p = (r, a) => `${(r * Math.cos(a)).toFixed(2)} ${(r * Math.sin(a)).toFixed(2)}`;
  return `M${p(ri, a0)} L${p(ro, a0)} A${ro} ${ro} 0 0 1 ${p(ro, a1)} L${p(ri, a1)} A${ri} ${ri} 0 0 0 ${p(ri, a0)} Z`;
}
export function Reloj24({ valores, color, unidad, modo = "barras", colorDe, dec = 2, etiquetaPico = "Más gasto", tam = 250 }) {
  const [sel, setSel] = useState(null);
  const vals = (valores || []).map((v) => (Number.isFinite(v) ? v : null));
  const max = Math.max(...vals.filter((v) => v !== null), 1e-9);
  const pico = vals.reduce((b, v, i) => (v !== null && (b === null || v > vals[b]) ? i : b), null);
  const r0 = modo === "barras" ? 54 : 64, r1 = 100;
  const h = sel !== null ? sel : pico;
  const fmtV = (v) => (v === null ? "sin datos" : `${nf(v, dec)} ${unidad}`);
  return (
    <svg viewBox="-128 -128 256 256" width="100%" style={{ display: "block", maxWidth: tam, margin: "0 auto" }} role="img"
      aria-label={pico !== null ? `${etiquetaPico}: a las ${pico} h (${fmtV(vals[pico])})` : "Sin datos"} onMouseLeave={() => setSel(null)}>
      {/* noche (de 22 a 7 h) */}
      <path d={(() => { const a0 = ang(22), a1 = ang(31); const p = (r, a) => `${r * Math.cos(a)} ${r * Math.sin(a)}`; return `M${p(r1 + 4, a0)} A${r1 + 4} ${r1 + 4} 0 0 1 ${p(r1 + 4, a1)} L0 0 Z`; })()} fill="#E4E9F3" />
      <circle r={r1 + 4} fill="none" stroke={V.line} strokeWidth="1.5" />
      {Array.from({ length: 24 }, (_, i) => {
        const v = vals[i];
        const ro = modo === "barras" ? (v ? r0 + Math.max(3, ((r1 - r0) * v) / max) : r0 + 2) : r1;
        const fill = modo === "barras" ? (v ? color : "#D5DACF") : v === null ? "#ECEEE9" : colorDe(v);
        return (
          <g key={i} onMouseEnter={() => setSel(i)} onClick={() => setSel(i)} style={{ cursor: "pointer" }}>
            {modo === "barras" && <path d={cuña(i, r0, r1)} fill={sel === i ? "rgba(31,39,29,0.08)" : "transparent"} />}
            <path d={cuña(i, r0, ro)} fill={fill} stroke={sel === i ? V.ink : "#FFFFFF"} strokeWidth={sel === i ? 2 : 1.2} />
            <title>{`${i}:00 a ${i + 1}:00 · ${fmtV(v)}`}</title>
          </g>
        );
      })}
      {Array.from({ length: 8 }, (_, k) => {
        const hh = k * 3, a = ang(hh), r = r1 + 17, grande = hh % 6 === 0;
        return <text key={hh} x={r * Math.cos(a)} y={r * Math.sin(a) + 4} textAnchor="middle" fontSize={grande ? 13 : 10.5} fontWeight={grande ? 800 : 600} fill={grande ? V.ink : V.ink3}>{hh}h</text>;
      })}
      {h !== null && (
        <g>
          <text y="-6" textAnchor="middle" fontSize="11" fontWeight="700" fill={V.ink3}>{sel !== null ? "A esa hora" : etiquetaPico}</text>
          <text y="15" textAnchor="middle" fontSize="19" fontWeight="800" fill={V.ink}>{h}–{h + 1} h</text>
          <text y="32" textAnchor="middle" fontSize="12" fontWeight="700" fill={V.ink2}>{fmtV(vals[h])}</text>
        </g>
      )}
    </svg>
  );
}

// ── Calendario de colores: cada día una casilla ─────────────────────────────
const DS = ["L", "M", "X", "J", "V", "S", "D"];
export function CalendarioColor({ dias, valores, colorDe, texto, hoy, titulo, compacto }) {
  const off = dias.length > 7 ? (new Date(dias[0] + "T12:00:00").getDay() + 6) % 7 : 0;
  const celdas = [...Array(off).fill(null), ...dias.map((d, i) => ({ d, v: valores[i] }))];
  return (
    <div>
      <div className="er-cal" style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: compacto ? 4 : 6 }}>
        {(dias.length > 7 ? DS : dias.map((d) => DS[(new Date(d + "T12:00:00").getDay() + 6) % 7])).map((s, i) => (
          <div key={"h" + i} style={{ textAlign: "center", fontSize: 12, fontWeight: 800, color: V.ink3 }}>{s}</div>
        ))}
        {celdas.map((c, i) => {
          if (!c) return <div key={i} />;
          const fut = c.d > hoy;
          const vacio = c.v === null || c.v === undefined;
          const bg = fut || vacio ? "#FFFFFF" : colorDe(c.v);
          const fg = fut || vacio ? V.ink3 : tinta(bg);
          return (
            <div key={i} title={`${c.d}: ${fut ? "todavía no ha llegado" : vacio ? "sin datos" : texto(c.v, true)}`}
              style={{ background: bg, color: fg, borderRadius: 10, minHeight: compacto ? 44 : dias.length > 7 ? 58 : 92, padding: "5px 6px", border: fut ? `1.5px dashed ${V.line}` : vacio ? `1.5px solid ${V.line}` : `1.5px solid ${mezcla(bg, "#000000", 0.15)}`,
                display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0, boxShadow: fut || vacio ? "none" : "inset 0 -3px 0 rgba(0,0,0,0.08)" }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, opacity: 0.85 }}>{Number(c.d.slice(8))}</span>
              <span style={{ fontSize: compacto ? 12 : dias.length > 7 ? 14 : 20, fontWeight: 800, textAlign: "center", lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fut ? "" : vacio ? "—" : texto(c.v, false)}</span>
            </div>
          );
        })}
      </div>
      {titulo}
    </div>
  );
}

// Leyenda de escala de color (de claro a oscuro, o frío-cómodo-calor)
export function EscalaColor({ tramos }) {
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 10, fontSize: 12.5, color: V.ink2, fontWeight: 600 }}>
      {tramos.map(([c, l]) => (
        <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 18, height: 12, borderRadius: 4, background: c, border: `1px solid ${mezcla(c, "#000000", 0.18)}` }} />{l}
        </span>
      ))}
    </div>
  );
}
